import { useState, useEffect } from 'react';
import axios from 'axios';
import { Sparkles, Check, Loader2, X, Calendar, ChevronDown, ChevronRight, Send, Instagram, Facebook, Newspaper, Mail, Archive, Download, Eye, Search, Filter, Layout, FileText, Megaphone } from 'lucide-react';

const API = '/api'; // Same-origin: /api routes on the serving domain (Preview or Production custom domain) to backend via Emergent ingress. Avoids cross-domain CORS / tracking-prevention failures.

/* ---- Mailchimp Archive Browser ---- */

// Exported: relocated into Newsletter Studio (2026-07-27 consolidation). The
// CampaignPlanner page itself is retired from the UI; this file stays as the
// home of these two browsers + the archived planner code.
export function ArchiveBrowser() {
  const [status, setStatus] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [t2tOnly, setT2tOnly] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewSubject, setPreviewSubject] = useState('');
  const [loadingPreview, setLoadingPreview] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/mailchimp/status`);
        setStatus(res.data);
      } catch (e) { console.error(e); }
    })();
  }, []);

  const fetchArchive = async (p, s, t2t) => {
    try {
      const params = new URLSearchParams({ page: p, per_page: 20 });
      if (s) params.append('search', s);
      if (t2t) params.append('t2t_only', 'true');
      const res = await axios.get(`${API}/mailchimp/archive?${params}`);
      setItems(res.data.items);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (e) { console.error(e); }
  };

  // `search` is intentionally omitted: page/filter changes refetch with the current
  // search term, but typing must not trigger a fetch — search is applied via handleSearch.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchArchive(page, search, t2tOnly); }, [page, t2tOnly]);

  const handleSearch = () => { setPage(1); fetchArchive(1, search, t2tOnly); };

  const handleImport = async () => {
    setImporting(true);
    try {
      const res = await axios.post(`${API}/mailchimp/import?max_import=50`);
      setImportResult(res.data);
      const statusRes = await axios.get(`${API}/mailchimp/status`);
      setStatus(statusRes.data);
    } catch (e) {
      setImportResult({ error: e.response?.data?.detail || 'Import failed' });
    }
    setImporting(false);
  };

  const handlePreview = async (mailchimpId, subject) => {
    setLoadingPreview(mailchimpId);
    try {
      const res = await axios.get(`${API}/mailchimp/preview/${mailchimpId}`);
      if (res.data.content_html) {
        setPreviewHtml(res.data.content_html);
        setPreviewSubject(subject);
      } else {
        setPreviewHtml(`<div style="padding:40px;text-align:center;color:#999;">No HTML template available for this campaign. Only text content was stored.</div>`);
        setPreviewSubject(subject);
      }
    } catch (e) { console.error(e); }
    setLoadingPreview('');
  };

  return (
    <div className="space-y-4">
      {/* Preview Modal */}
      {previewHtml && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setPreviewHtml(null)}>
          <div className="bg-white rounded-sm max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="bg-hg-surface flex items-center justify-between px-4 py-3 border-b border-hg-border">
              <span className="text-sm font-sans text-hg-text truncate">{previewSubject}</span>
              <button onClick={() => setPreviewHtml(null)} className="text-hg-text-dim hover:text-hg-text ml-3"><X size={18} /></button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <iframe
                srcDoc={previewHtml}
                title="Newsletter Preview"
                className="w-full h-full min-h-[600px] border-0"
                sandbox="allow-same-origin"
              />
            </div>
          </div>
        </div>
      )}

      {/* Status Bar */}
      {status && (
        <div className="bg-hg-surface border border-hg-border rounded-sm p-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Archive size={14} className="text-hg-green" strokeWidth={1.5} />
                <span className="text-sm font-sans text-hg-text">{status.imported} campaigns</span>
              </div>
              <span className="text-xs text-hg-green font-sans">{status.t2t_imported} T2T newsletters</span>
              {status.remaining > 0 && (
                <span className="text-xs text-hg-text-dim font-sans">{status.remaining} remaining</span>
              )}
            </div>
            {status.remaining > 0 && (
              <button
                onClick={handleImport}
                disabled={importing}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:bg-hg-green-hover transition-colors disabled:opacity-40"
              >
                {importing ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                {importing ? 'Importing...' : `Import Next 50`}
              </button>
            )}
          </div>
          {importResult && importResult.success && (
            <p className="text-xs text-hg-green font-sans mt-2">Imported {importResult.imported} campaigns</p>
          )}
        </div>
      )}

      {/* Search + Filter */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-hg-text-dim" />
          <input
            data-testid="archive-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Search campaigns..."
            className="w-full bg-hg-bg border border-hg-border rounded-sm pl-9 pr-3 py-2 text-sm text-hg-text font-sans placeholder-hg-text-dim focus:outline-none focus:ring-1 focus:ring-hg-green"
          />
        </div>
        <button
          onClick={() => { setT2tOnly(!t2tOnly); setPage(1); }}
          className={`flex items-center gap-1.5 px-3 py-2 text-xs font-sans rounded-sm border transition-colors ${
            t2tOnly ? 'border-hg-green bg-hg-green/10 text-hg-green' : 'border-hg-border text-hg-text-dim hover:border-hg-green'
          }`}
        >
          <Filter size={12} /> T2T Only
        </button>
        <button onClick={handleSearch} className="px-3 py-2 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:bg-hg-green-hover">
          Search
        </button>
      </div>

      {/* Campaign List */}
      <div className="space-y-1">
        {items.map((item) => (
          <div key={item.mailchimp_id} className="bg-hg-surface border border-hg-border rounded-sm p-3 flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-sans text-hg-text truncate">{item.subject || '(No subject)'}</span>
                {item.is_t2t && (
                  <span className="text-[10px] font-sans text-hg-green bg-hg-green/10 px-1.5 py-0.5 rounded-sm flex-shrink-0">T2T</span>
                )}
                {item.has_template && (
                  <span className="text-[10px] font-sans text-blue-400 bg-blue-400/10 px-1.5 py-0.5 rounded-sm flex-shrink-0">HTML</span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-[10px] text-hg-text-dim font-sans">
                  {item.send_time ? new Date(item.send_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'No date'}
                </span>
                {item.from_name && <span className="text-[10px] text-hg-text-dim font-sans">{item.from_name}</span>}
              </div>
            </div>
            <button
              data-testid={`preview-${item.mailchimp_id}`}
              onClick={() => handlePreview(item.mailchimp_id, item.subject)}
              disabled={loadingPreview === item.mailchimp_id}
              className="flex items-center gap-1 px-2 py-1 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:border-hg-green hover:text-hg-green transition-colors flex-shrink-0 disabled:opacity-40"
            >
              {loadingPreview === item.mailchimp_id ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />}
              View
            </button>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="px-3 py-1 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:text-hg-text disabled:opacity-30"
          >
            Previous
          </button>
          <span className="text-xs text-hg-text-dim font-sans">Page {page} of {pages} ({total} total)</span>
          <button
            onClick={() => setPage(Math.min(pages, page + 1))}
            disabled={page === pages}
            className="px-3 py-1 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:text-hg-text disabled:opacity-30"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

/* ---- Week Card ---- */

function WeekCard({ week, campaignId, onQueued }) {
  const [expanded, setExpanded] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [queuing, setQueuing] = useState(false);
  const [queued, setQueued] = useState(false);
  const [weekItems, setWeekItems] = useState(week.items || []);
  const hasContent = week.content_generated || weekItems.some(i => i.copy || i.body || i.sections);

  const phaseColors = {
    TEASE: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20',
    LAUNCH: 'text-hg-green bg-hg-green/10 border-hg-green/20',
    EDUCATE: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
    CONVERT: 'text-orange-400 bg-orange-400/10 border-orange-400/20',
  };
  const typeIcons = { social: Instagram, newsletter: Newspaper, email: Mail };

  const handleGenerateContent = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/campaign/${campaignId}/generate-week/${week.week}`);
      setWeekItems(res.data.items || []);
    } catch (e) { console.error(e); }
    setGenerating(false);
  };

  const handleQueue = async () => {
    setQueuing(true);
    try {
      await axios.post(`${API}/campaign/${campaignId}/queue-week/${week.week}`);
      setQueued(true);
      onQueued && onQueued(week.week);
    } catch (e) { console.error(e); }
    setQueuing(false);
  };

  return (
    <div className="bg-hg-surface border border-hg-border rounded-sm overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 lg:p-4 hover:bg-hg-surface-hover transition-colors"
      >
        <div className="flex items-center gap-2 lg:gap-3 flex-wrap">
          {expanded ? <ChevronDown size={14} className="text-hg-text-dim" /> : <ChevronRight size={14} className="text-hg-text-dim" />}
          <span className="text-sm font-sans text-hg-text font-medium">Week {week.week}</span>
          <span className={`text-[10px] font-sans uppercase tracking-wider px-2 py-0.5 rounded-sm border ${phaseColors[week.phase] || 'text-hg-text-dim bg-hg-bg border-hg-border'}`}>
            {week.phase}
          </span>
          <span className="text-xs text-hg-text-dim font-sans hidden sm:inline">{week.theme}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-hg-text-dim font-sans">{weekItems.length} items</span>
          {hasContent && <span className="text-[10px] text-hg-green font-sans hidden sm:inline">Content Ready</span>}
          {queued && <Check size={14} className="text-hg-green" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-hg-border p-3 lg:p-4 space-y-3">
          <p className="text-xs text-hg-text-dim font-sans sm:hidden">{week.theme}</p>
          {weekItems.map((item, i) => {
            const Icon = typeIcons[item.type] || Mail;
            const hasCopy = item.copy || item.body || item.sections;
            return (
              <div key={i} className="bg-hg-bg border border-hg-border rounded-sm p-3 space-y-1">
                <div className="flex items-center gap-2">
                  <Icon size={12} className="text-hg-green" strokeWidth={1.5} />
                  <span className="text-[10px] font-sans text-hg-text-dim uppercase tracking-wider">
                    {item.type === 'social' ? `${item.platform || 'Social'} — ${item.angle}` : item.type === 'newsletter' ? 'Newsletter' : `Email → ${item.audience || 'Customers'}`}
                  </span>
                </div>
                {hasCopy ? (
                  <p className="text-sm font-sans text-hg-text/90 whitespace-pre-line line-clamp-4">
                    {item.copy || item.body || item.subject || (item.sections?.map(s => s.title).join(' · '))}
                  </p>
                ) : (
                  <p className="text-xs font-sans text-hg-text-dim italic">{item.brief}</p>
                )}
                {item.note && <p className="text-xs text-hg-text-dim italic font-sans">{item.note}</p>}
              </div>
            );
          })}
          <div className="flex gap-2">
            {!hasContent && (
              <button
                onClick={handleGenerateContent}
                disabled={generating}
                className="flex-1 flex items-center justify-center gap-2 py-2 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:bg-hg-green-hover transition-colors disabled:opacity-40"
              >
                {generating ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                {generating ? 'Writing content...' : 'Generate Full Content'}
              </button>
            )}
            {hasContent && (
              <button
                onClick={handleQueue}
                disabled={queuing || queued}
                className={`flex-1 flex items-center justify-center gap-2 py-2 text-xs font-sans rounded-sm transition-colors ${
                  queued ? 'bg-hg-green/10 text-hg-green border border-hg-green/20' : 'bg-hg-green text-hg-text hover:bg-hg-green-hover'
                }`}
              >
                {queuing ? <Loader2 size={12} className="animate-spin" /> : queued ? <Check size={12} /> : <Send size={12} />}
                {queuing ? 'Queuing...' : queued ? 'Sent to Approval Queue' : 'Send to Approval Queue'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---- HG Template Gallery ---- */

export function TemplateGallery() {
  const [templates, setTemplates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState('newsletter');
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/templates/gallery`);
        setTemplates(res.data.templates);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  const tplMeta = {
    newsletter: { icon: Newspaper, label: 'T2T Newsletter', tagline: 'Long-form, multi-section' },
    promotional: { icon: Mail, label: 'Promotional Email', tagline: 'Hero + body + CTA' },
    announcement: { icon: Megaphone, label: 'Announcement', tagline: 'Short, high-impact' },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={20} className="animate-spin text-hg-green" />
      </div>
    );
  }

  if (!templates) {
    return <div className="text-center py-12 text-hg-text-dim text-sm font-sans">Couldn't load templates.</div>;
  }

  const activeTpl = templates[active];

  return (
    <div className="space-y-4" data-testid="template-gallery">
      {/* Header */}
      <div className="bg-hg-surface border border-hg-border rounded-sm p-4 lg:p-5">
        <div className="flex items-start gap-3">
          <Layout size={20} className="text-hg-green mt-0.5 flex-shrink-0" strokeWidth={1.5} />
          <div>
            <h2 className="font-display text-base lg:text-lg text-hg-text tracking-wider">HG SIGNATURE TEMPLATES</h2>
            <p className="text-xs text-hg-text-dim font-sans mt-1 leading-relaxed">
              Three branded formats inspired by your most recent Mailchimp sends. Every newsletter, email, and announcement pushed from this hub is automatically formatted into the matching template.
            </p>
          </div>
        </div>
      </div>

      {/* Template switcher */}
      <div className="grid grid-cols-3 gap-2 lg:gap-3">
        {Object.entries(tplMeta).map(([key, meta]) => {
          const Icon = meta.icon;
          const isActive = active === key;
          return (
            <button
              key={key}
              data-testid={`template-tab-${key}`}
              onClick={() => setActive(key)}
              className={`text-left p-3 lg:p-4 rounded-sm border transition-colors ${
                isActive ? 'border-hg-green bg-hg-green/5' : 'border-hg-border bg-hg-surface hover:border-hg-green/40'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon size={14} className={isActive ? 'text-hg-green' : 'text-hg-text-dim'} strokeWidth={1.5} />
                <span className={`text-xs lg:text-sm font-sans ${isActive ? 'text-hg-text' : 'text-hg-text-dim'}`}>{meta.label}</span>
              </div>
              <p className="text-[10px] lg:text-[11px] text-hg-text-dim font-sans hidden sm:block">{meta.tagline}</p>
            </button>
          );
        })}
      </div>

      {/* Description + Preview */}
      {activeTpl && (
        <div className="space-y-3">
          <div className="bg-hg-bg border border-hg-border rounded-sm p-3 lg:p-4">
            <div className="flex items-start justify-between gap-3">
              <p className="text-xs lg:text-sm text-hg-text-dim font-sans leading-relaxed">{activeTpl.description}</p>
              <button
                onClick={() => setFullscreen(true)}
                className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:bg-hg-green-hover transition-colors"
              >
                <Eye size={12} /> Full Preview
              </button>
            </div>
          </div>

          <div className="bg-white rounded-sm overflow-hidden border border-hg-border">
            <iframe
              key={active}
              srcDoc={activeTpl.html}
              title={activeTpl.label}
              className="w-full h-[600px] border-0"
              sandbox="allow-same-origin"
            />
          </div>
        </div>
      )}

      {/* Fullscreen modal */}
      {fullscreen && activeTpl && (
        <div
          className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4"
          onClick={() => setFullscreen(false)}
        >
          <div className="bg-white rounded-sm w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="bg-hg-surface flex items-center justify-between px-4 py-3 border-b border-hg-border">
              <span className="text-sm font-sans text-hg-text">{activeTpl.label}</span>
              <button onClick={() => setFullscreen(false)} className="text-hg-text-dim hover:text-hg-text"><X size={18} /></button>
            </div>
            <iframe srcDoc={activeTpl.html} title={activeTpl.label} className="w-full flex-1 border-0" sandbox="allow-same-origin" />
          </div>
        </div>
      )}
    </div>
  );
}


/* ---- Main Component ---- */

export default function CampaignPlanner() {
  const [idea, setIdea] = useState('');
  const [weeks, setWeeks] = useState(4);
  const [notes, setNotes] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [currentPlan, setCurrentPlan] = useState(null);
  const [currentCampaignId, setCurrentCampaignId] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [activeTab, setActiveTab] = useState('create');
  const [notification, setNotification] = useState('');

  const showNotif = (msg) => { setNotification(msg); setTimeout(() => setNotification(''), 4000); };

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/campaign/list`);
        setCampaigns(res.data.campaigns);
      } catch (e) { console.error(e); }
    })();
  }, []);

  const handleCreate = async () => {
    if (!idea.trim()) return;
    setGenerating(true);
    setError('');
    setCurrentPlan(null);
    try {
      const res = await axios.post(`${API}/campaign/create`, {
        idea: idea.trim(), duration_weeks: weeks,
        channels: ['social', 'newsletter', 'email'],
        notes: notes.trim() || null,
      });
      setCurrentPlan(res.data.plan);
      setCurrentCampaignId(res.data.campaign_id);
      showNotif(`Campaign plan created: ${res.data.plan?.total_weeks || weeks} weeks`);
      const listRes = await axios.get(`${API}/campaign/list`);
      setCampaigns(listRes.data.campaigns);
    } catch (e) {
      setError(e.response?.data?.detail || 'Campaign planning failed');
    }
    setGenerating(false);
  };

  return (
    <div className="space-y-6">
      {notification && (
        <div className="fixed top-4 right-4 z-50 bg-hg-surface border border-hg-green/30 rounded-sm px-4 py-3 text-sm text-hg-green font-sans flex items-center gap-2 animate-fade-in">
          <Check size={14} /> {notification}
        </div>
      )}

      <div>
        <h1 className="font-display text-2xl lg:text-3xl text-hg-text tracking-wider">CAMPAIGN PLANNER</h1>
        <p className="text-sm text-hg-text-dim font-sans mt-1">Plan multi-week rollouts across social, newsletter & email.</p>
      </div>

      <div className="flex gap-2 border-b border-hg-border overflow-x-auto">
        {[
          { id: 'create', label: 'Plan Campaign' },
          { id: 'history', label: `Past Campaigns (${campaigns.length})` },
          { id: 'templates', label: 'HG Templates' },
          { id: 'archive', label: 'Newsletter Archive' },
        ].map((tab) => (
          <button
            key={tab.id}
            data-testid={`campaign-tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-sm font-sans transition-colors border-b-2 -mb-px whitespace-nowrap ${
              activeTab === tab.id ? 'border-hg-green text-hg-green' : 'border-transparent text-hg-text-dim hover:text-hg-text'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'create' && (
        <div className="space-y-6">
          <div className="bg-hg-surface border border-hg-border rounded-sm p-4 lg:p-6 space-y-5">
            <div>
              <label className="block text-xs font-sans text-hg-text-dim uppercase tracking-wider mb-2">Campaign Idea</label>
              <textarea
                data-testid="campaign-idea-input"
                value={idea}
                onChange={(e) => setIdea(e.target.value)}
                placeholder="E.g. 'Fly Series driver launch, fittings open March 1'..."
                className="w-full bg-hg-bg border border-hg-border rounded-sm p-3 text-sm text-hg-text font-sans placeholder-hg-text-dim focus:outline-none focus:ring-1 focus:ring-hg-green resize-none"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-sans text-hg-text-dim uppercase tracking-wider mb-2">Duration</label>
                <div className="flex gap-2">
                  {[2, 4, 6].map((w) => (
                    <button key={w} data-testid={`duration-${w}`} onClick={() => setWeeks(w)}
                      className={`flex-1 px-3 py-2 text-sm font-sans rounded-sm border transition-colors ${
                        weeks === w ? 'border-hg-green bg-hg-green/10 text-hg-green' : 'border-hg-border text-hg-text-dim hover:border-hg-green/50'
                      }`}
                    >{w} Weeks</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-sans text-hg-text-dim uppercase tracking-wider mb-2">Notes (optional)</label>
                <input data-testid="campaign-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)}
                  placeholder="Target audience, key dates..."
                  className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans placeholder-hg-text-dim focus:outline-none focus:ring-1 focus:ring-hg-green"
                />
              </div>
            </div>
            <button data-testid="create-campaign-btn" onClick={handleCreate} disabled={generating || !idea.trim()}
              className="flex items-center gap-2 bg-hg-green text-hg-text font-sans font-medium text-sm px-5 py-2.5 rounded-sm hover:bg-hg-green-hover transition-colors disabled:opacity-40"
            >
              {generating ? <Loader2 size={16} className="animate-spin" /> : <Calendar size={16} />}
              {generating ? 'Planning campaign...' : 'Generate Campaign Plan'}
            </button>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-sm p-4 text-sm text-red-400 font-sans flex items-center gap-2">
              <X size={14} /> {error}
            </div>
          )}

          {generating && (
            <div className="bg-hg-surface border border-hg-border rounded-sm p-8 text-center space-y-3">
              <div className="flex items-center justify-center gap-3">
                <div className="w-2 h-2 rounded-full bg-hg-green animate-pulse" />
                <div className="w-2 h-2 rounded-full bg-hg-green animate-pulse" style={{ animationDelay: '300ms' }} />
                <div className="w-2 h-2 rounded-full bg-hg-green animate-pulse" style={{ animationDelay: '600ms' }} />
              </div>
              <p className="text-sm text-hg-text-dim font-sans">AI is planning your {weeks}-week rollout across all channels...</p>
            </div>
          )}

          {currentPlan && (
            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <h2 className="font-display text-lg lg:text-xl text-hg-text tracking-wider">{currentPlan.campaign_name || 'CAMPAIGN PLAN'}</h2>
                <span className="text-xs text-hg-text-dim font-sans">{currentPlan.total_weeks || weeks} weeks</span>
              </div>
              {(currentPlan.weeks || []).map((week) => (
                <WeekCard key={week.week} week={week} campaignId={currentCampaignId}
                  onQueued={(w) => showNotif(`Week ${w} sent to approval queue`)} />
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'history' && (
        <div className="space-y-3">
          {campaigns.length === 0 && (
            <div className="text-center py-12 text-hg-text-dim text-sm font-sans">No campaigns yet.</div>
          )}
          {campaigns.map((c) => (
            <div key={c.id} className="bg-hg-surface border border-hg-border rounded-sm p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-sans text-hg-text font-medium">{c.plan?.campaign_name || c.idea}</span>
                <span className="text-xs text-hg-text-dim font-sans">
                  {new Date(c.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
              </div>
              <p className="text-xs text-hg-text-dim font-sans">{c.idea}</p>
              <div className="flex items-center gap-3 mt-2">
                <span className="text-[10px] text-hg-text-dim font-sans">{c.duration_weeks} weeks</span>
                <span className="text-[10px] text-hg-text-dim font-sans">{c.plan?.weeks?.reduce((sum, w) => sum + (w.items?.length || 0), 0) || 0} items</span>
              </div>
              <button
                onClick={() => { setCurrentPlan(c.plan); setCurrentCampaignId(c.id); setActiveTab('create'); }}
                className="mt-2 text-xs text-hg-green hover:text-hg-green-hover font-sans transition-colors"
              >
                View Plan
              </button>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'templates' && <TemplateGallery />}
      {activeTab === 'archive' && <ArchiveBrowser />}
    </div>
  );
}
